import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../shared/services/toast.service';
import { EmailChannelPolicyApiService } from '../services/email-channel-policy-api.service';
import { BusinessApiService } from '../../businesses/services/business-api.service';
import {
  CreateEmailChannelPolicyRequest,
  EmailChannelPolicy,
  EmailPolicyChannelType,
  UpdateEmailChannelPolicyRequest,
} from '../models/email-channel-policy.model';

@Component({
  selector: 'app-email-channel-policy-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './email-channel-policy-form.component.html',
})
export class EmailChannelPolicyFormComponent {
  @Input() drawerMode = true;

  @Output() saved = new EventEmitter<EmailChannelPolicy>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  /** Nombre de la empresa dueña de la política (se muestra en vez del id al editar). */
  businessName: string | null = null;
  saving = false;
  error = '';
  isEditing = false;
  policyId: number | null = null;

  readonly channelTypes: { value: EmailPolicyChannelType; label: string }[] = [
    { value: 'SYSTEM_API', label: 'Sistema (API)' },
    { value: 'USER_GMAIL', label: 'Gmail' },
    { value: 'USER_MICROSOFT', label: 'Outlook / Microsoft' },
    { value: 'USER_SMTP', label: 'SMTP' },
    { value: 'USER_API', label: 'API de usuario' },
  ];

  constructor(
    private fb: FormBuilder,
    private api: EmailChannelPolicyApiService,
    private businessApi: BusinessApiService,
    private toast: ToastService
  ) {
    this.initForm();
  }

  channelLabel(type: EmailPolicyChannelType | null | undefined): string {
    return this.channelTypes.find((c) => c.value === type)?.label ?? (type ?? '');
  }

  private initForm(): void {
    this.form = this.fb.group({
      channelType: ['SYSTEM_API' as EmailPolicyChannelType, [Validators.required]],
      intIdBussines: [null as number | null],
      dailyLimit: [0, [Validators.required, Validators.min(0)]],
      perMinuteLimit: [0, [Validators.required, Validators.min(0)]],
      burst: [null as number | null, [Validators.min(0)]],
      enabled: [true],
    });
  }

  reset(): void {
    this.isEditing = false;
    this.policyId = null;
    this.businessName = null;
    this.error = '';
    this.saving = false;
    this.initForm();
  }

  /** Carga una política existente para editar (channelType e intIdBussines readonly). */
  loadPolicy(policy: EmailChannelPolicy): void {
    this.isEditing = true;
    this.policyId = policy.id;
    this.error = '';
    // El alcance se muestra por NOMBRE de empresa; el id es un dato interno.
    this.businessName = null;
    if (policy.intIdBussines !== null && policy.intIdBussines !== undefined) {
      this.businessApi.getById(policy.intIdBussines).subscribe({
        next: (b) => { this.businessName = b?.name ?? null; },
        error: () => { this.businessName = null; }
      });
    }
    this.form.patchValue({
      channelType: policy.channelType,
      intIdBussines: policy.intIdBussines,
      dailyLimit: policy.dailyLimit,
      perMinuteLimit: policy.perMinuteLimit,
      burst: policy.burst,
      enabled: policy.enabled,
    });
    // channelType e intIdBussines no se pueden cambiar al editar.
    this.form.get('channelType')?.disable();
    this.form.get('intIdBussines')?.disable();
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';

    // getRawValue() incluye los controles deshabilitados (channelType/intIdBussines).
    const v = this.form.getRawValue();
    const dailyLimit = Number(v.dailyLimit) || 0;
    const perMinuteLimit = Number(v.perMinuteLimit) || 0;
    const burst = v.burst === null || v.burst === '' || v.burst === undefined ? null : Number(v.burst);

    const req$ = this.isEditing && this.policyId
      ? this.api.update(this.policyId, {
          dailyLimit,
          perMinuteLimit,
          burst,
          enabled: !!v.enabled,
        } as UpdateEmailChannelPolicyRequest)
      : this.api.create({
          channelType: v.channelType,
          intIdBussines:
            v.intIdBussines === null || v.intIdBussines === '' || v.intIdBussines === undefined
              ? null
              : Number(v.intIdBussines),
          dailyLimit,
          perMinuteLimit,
          burst,
          enabled: !!v.enabled,
        } as CreateEmailChannelPolicyRequest);

    req$.subscribe({
      next: (policy) => {
        this.saving = false;
        if (this.isEditing) {
          this.toast.updated('Política de canal');
        } else {
          this.toast.created('Política de canal');
        }
        this.saved.emit(policy);
      },
      error: (err) => {
        this.saving = false;
        const msg = err?.error?.message || 'Error al guardar la política de canal';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  cancelar(): void {
    this.cancelled.emit();
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['min']) return `El valor mínimo es ${c.errors['min'].min}`;
    return 'Campo inválido';
  }
}
